import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer, PageHeader, Button, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useOrgStore } from '@/stores/orgStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { OrganizationSettingsService } from '@/services/OrganizationSettingsService';
import { Loader2, Save, Calendar } from 'lucide-react';

const settingsService = new OrganizationSettingsService();

export function GeneralSettings() {
  const { activeOrgId } = useOrgStore();
  const queryClient = useQueryClient();
  const notificationStore = useNotificationStore();
  
  const [proposalReviewCallLink, setProposalReviewCallLink] = useState('');
  const [feedbackNotificationEmails, setFeedbackNotificationEmails] = useState('');

  // Fetch settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['organization-settings', activeOrgId],
    queryFn: () => settingsService.getSettings(activeOrgId!),
    enabled: !!activeOrgId,
  });

  // Update form when settings load
  useEffect(() => {
    if (settings) {
      setProposalReviewCallLink(settings.proposal_review_call_link || '');
      setFeedbackNotificationEmails(settings.proposal_feedback_notification_emails || '');
    }
  }, [settings]);

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: (data: { proposal_review_call_link?: string | null; proposal_feedback_notification_emails?: string | null }) =>
      settingsService.updateSettings(activeOrgId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization-settings', activeOrgId] });
      notificationStore.success('Settings updated successfully');
    },
    onError: (error: any) => {
      notificationStore.error(error.message || 'Failed to update settings');
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      proposal_review_call_link: proposalReviewCallLink || null,
      proposal_feedback_notification_emails: feedbackNotificationEmails || null,
    });
  };

  const hasChanges = 
    proposalReviewCallLink !== (settings?.proposal_review_call_link || '') ||
    feedbackNotificationEmails !== (settings?.proposal_feedback_notification_emails || '');

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Organization Settings"
        description="Configure organization-wide settings for proposals and notifications"
      >
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateMutation.isPending}
          isLoading={updateMutation.isPending}
          leftIcon={<Save className="w-4 h-4" />}
        >
          Save Changes
        </Button>
      </PageHeader>

      <div className="mt-6 space-y-6">
        {/* Proposal Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-600" />
              Proposal Settings
            </CardTitle>
            <CardDescription>
              Configure settings for proposal review calls and feedback notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Calendly Link */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Proposal Review Call Link (Calendly)
              </label>
              <Input
                type="url"
                value={proposalReviewCallLink}
                onChange={(e) => setProposalReviewCallLink(e.target.value)}
                placeholder="https://calendly.com/your-username/review-call"
                hint="This link will be used when clients click 'Book a Review Call' on the proposal page"
              />
            </div>

            {/* Feedback Notification Emails */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Feedback Notification Emails
              </label>
              <Textarea
                value={feedbackNotificationEmails}
                onChange={(e) => setFeedbackNotificationEmails(e.target.value)}
                placeholder="email1@example.com, email2@example.com"
                rows={3}
                hint="Comma-separated list of email addresses to notify when clients submit feedback. Leave empty to disable notifications."
              />
              <p className="text-xs text-muted-foreground mt-1">
                When feedback is submitted, these email addresses will receive a notification with the client name, event name, submitter's name, and comments.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
